import React, { ReactNode, useContext, useEffect, useRef, useState } from "react"
import { v4 } from "uuid"
import { getArticlePathName, toShortArticlePath } from "./Client/articleTrees/ArticleTreeResolver"
import { useClient } from "./Client/ClientAndUserProvider"
import { ClientInstance } from "./Client/ClientInstance"
import { GoogleTagManagerMode } from "./Client/FeatureTypes"
import { GetGoogleTagManagerSettingsPublicResponse } from "./CustomerPortal/GoogleTagManagerSettings/GoogleTagManagerSettingsModel"
import { discountOneLineSummary } from "./CustomerPortal/OrderDetails/ExplainDiscount/ExplainDiscount"
import { API } from "./network/API"
import { OrderItem, OrderItemProduct } from "./Orders/order-data-model"
import { notNull } from "./Shared/notNull"

type Props = {
	children: ReactNode
}

type GTMDATA = string | number | { [key: string]: GTMDATA | GTMDATA[] }

type GTMEvent = {
	event: string
	[key: string]: GTMDATA
}

declare global {
	interface Window {
		dataLayer: GTMEvent[]
	}
}

export class GoogleTagManagerInstance {
	public pushEvent(eventName: string, eventData: { [key: string]: GTMDATA }) {
		if (this.client.features.googleTagManagerMode === GoogleTagManagerMode.Disabled) {
			return
		}

		window["dataLayer"] = window["dataLayer"] || []

		window["dataLayer"].push({
			event: eventName,
			...eventData,
		})
	}

	public orderItemToGtmItemWithPrices(client: ClientInstance, orderItem: OrderItem): GTMDATA[] {
		const productLines = orderItem.products
			.map((product: OrderItemProduct) => {
				const article = product?.articles?.getProductArticle()

				if (!article) {
					return null
				}

				let quantity

				if (product.packagingMethod) {
					quantity =
						product.packagingMethod.amount *
						(client.possiblePackagingMethods[product.packagingMethod.id]?.multiplier || 1)
				} else {
					quantity = product?.wasteType?.amount || product.amount || 1
				}

				const priceNoTax = article.price * quantity

				return {
					item_id: product.productId,
					item_name: product.name,
					price: priceNoTax,
					quantity: quantity,
					item_category: product.category,
				}
			})
			.filter(notNull)

		const discountLines = (orderItem.articles?.getDiscountArticles() || [])
			.map((discountArticleAndTree) => {
				let discount = discountArticleAndTree.article.price
				const discountName =
					discountOneLineSummary(discountArticleAndTree.article.discountDescription, null, 0) ??
					getArticlePathName(discountArticleAndTree.article.articlePath)

				return {
					item_id: toShortArticlePath(discountArticleAndTree.article.articlePath),
					item_name: discountName,
					price: 0,
					discount: discount,
					quantity: 1,
					item_variant: "discount",
				}
			})
			.filter(notNull)

		let transportLines
		const transportPriceArticlesAndTrees = orderItem?.articles?.getTransportationArticles()

		if (transportPriceArticlesAndTrees) {
			transportLines = transportPriceArticlesAndTrees.map((transportPriceArticleAndTree) => {
				return {
					item_id: toShortArticlePath(transportPriceArticleAndTree.article.articlePath),
					item_name: getArticlePathName(transportPriceArticleAndTree.article.articlePath),
					price: transportPriceArticleAndTree.article.price,
					quantity: 1,
					item_variant: "transportation_article",
				}
			})
		}

		let dateLines
		const dateSlotPriceArticle = orderItem?.articles?.getDateSlotArticle()

		if (dateSlotPriceArticle) {
			dateLines = [
				{
					item_id: toShortArticlePath(dateSlotPriceArticle.articlePath),
					item_name: getArticlePathName(dateSlotPriceArticle.articlePath),
					price: dateSlotPriceArticle.price,
					quantity: 1,
					item_variant: "date_article",
				},
			]
		}

		return [...productLines, ...discountLines, ...(transportLines || []), ...(dateLines || [])]
	}

	public orderItemToGtmItemWithoutPrices(client: ClientInstance, orderItem: OrderItem): GTMDATA[] {
		return orderItem.products
			.map((product: OrderItemProduct) => {
				let quantity

				if (product.packagingMethod) {
					quantity =
						product.packagingMethod.amount *
						(client.possiblePackagingMethods[product.packagingMethod.id]?.multiplier || 1)
				} else {
					quantity = product?.wasteType?.amount || product.amount || 1
				}

				return {
					item_id: product.productId,
					item_name: product.name,
					price: 0,
					quantity: quantity,
					item_category: product.category,
				}
			})
			.filter(notNull)
	}

	constructor(private readonly client: ClientInstance) {}
}

const GoogleTagManagerContext = React.createContext<GoogleTagManagerInstance | null>(null)

export function GoogleTagManagerContextProvider({ children }: Props) {
	const client = useClient()

	const scriptTagId = useRef(v4())

	const [gtmInstance, setGtmInstance] = useState<GoogleTagManagerInstance>(new GoogleTagManagerInstance(client))

	useEffect(() => {
		if (client.features.googleTagManagerMode === GoogleTagManagerMode.Disabled) {
			return
		}
		API.getWithRetries<GetGoogleTagManagerSettingsPublicResponse>(
			`/customer-portal/google-tag-manager-settings-v1/${client.identifier}/public`,
			true,
		)
			.then((response) => {
				const settings = response?.googleTagManagerSettings || null
				if (settings) {
					create(settings.tagManagerId)
				}
				setGtmInstance(new GoogleTagManagerInstance(client))
			})
			.catch(() => {})
	}, [client])

	function create(gtmId: string) {
		window["dataLayer"] = window["dataLayer"] || []
		window["dataLayer"].push({ "gtm.start": new Date().getTime(), event: "gtm.js" })

		let newScriptTag = document.createElement("script")

		newScriptTag.id = scriptTagId.current
		newScriptTag.async = true
		newScriptTag.src = "https://www.googletagmanager.com/gtm.js?id=" + gtmId
		document.head.insertBefore(newScriptTag, document.head.firstChild)
	}

	return <GoogleTagManagerContext.Provider value={gtmInstance}>{children}</GoogleTagManagerContext.Provider>
}

const useGoogleTagManagerContext = () => useContext(GoogleTagManagerContext)

export function useGoogleTagManager(): GoogleTagManagerInstance {
	const context = useGoogleTagManagerContext()
	if (!context) {
		throw new Error("GoogleTagManagerContext does not exist")
	}

	return context
}
